import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Paper, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, CreditCard as CardIcon, Delete as DeleteIcon, Edit as EditIcon, Info as InfoIcon,
  History as HistoryIcon, WarningAmber as WarningIcon, AddCircle as AddedIcon, Edit as ChangedIcon, RemoveCircle as RemovedIcon, Close as CloseIcon,
} from '@mui/icons-material';
import CardTransactionsDialog from '../../components/cards/CardTransactionsDialog';
import CustomerCardDialog from '../../components/cards/CustomerCardDialog';
import CardFace, { isCardExpired, paletteFor } from '../../components/cards/CardFace';
import { customerCardService } from '../../services/accounting/customerCard.service';
import { serverMessage } from '../../utils/apiConvert';
import { formatDateTime } from '../../utils/dateFormat';
import { describeCardEvent, ACTOR_LABELS } from '../../components/cards/cardEvents';

/**
 * Client › Saved cards: the customer's cards (metadata only) the TC picks when a government fee is paid by the
 * customer's card. The client manages them on the portal (BE-8); the TC can add, edit and remove them here too
 * (BE-8b). Every change from either side is in the card activity (audit log).
 */

const FS = '0.8125rem';

const ACTION_STYLE = {
  CREATED: { Icon: AddedIcon, color: 'success.main' },
  UPDATED: { Icon: ChangedIcon, color: 'info.main' },
  REMOVED: { Icon: RemovedIcon, color: 'error.main' },
};

// A small coloured swatch of the card (its CardFace colours) so rows of different cards are easy to tell apart
const Swatch = ({ card }) => {
  const [from, to] = card ? paletteFor(card) : ['#9e9e9e', '#bdbdbd'];
  return <Box sx={{ width: 22, height: 14, borderRadius: 0.5, flexShrink: 0, background: `linear-gradient(135deg, ${from}, ${to})` }} />;
};

const TimelineRow = ({ e, card, showCard, last, onPickCard }) => {
  const { Icon, color } = ACTION_STYLE[e.action] || { Icon: HistoryIcon, color: 'text.secondary' };
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {/* rail */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
        <Icon sx={{ fontSize: 20, color, mt: 0.25 }} />
        {!last && <Box sx={{ flex: 1, width: '2px', bgcolor: 'divider', my: 0.5 }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: FS, fontWeight: 600 }}>{describeCardEvent(e)}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 'auto', whiteSpace: 'nowrap' }}>
            {formatDateTime(e.occurredAt)}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          by <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{e.actorName || 'Unknown'}</Box>
          {ACTOR_LABELS[e.actorType] ? ` · ${ACTOR_LABELS[e.actorType]}` : ''}
        </Typography>
        {showCard && (
          <Box
            component={card ? 'button' : 'div'}
            onClick={card ? () => onPickCard(card.id) : undefined}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 0.5, px: 0.75, py: 0.25,
              border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper',
              font: 'inherit', cursor: card ? 'pointer' : 'default', color: 'text.primary',
              '&:hover': card ? { borderColor: 'primary.main' } : {},
            }}
          >
            <Swatch card={card} />
            <Typography sx={{ fontSize: '0.75rem' }}>{e.cardLabel}</Typography>
            {!card && <Typography sx={{ fontSize: '0.6875rem', color: 'error.main', fontWeight: 600 }}>removed</Typography>}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const NETWORK_NAMES = { VISA: 'Visa', MASTERCARD: 'Mastercard', AMEX: 'American Express' };

const Field = ({ label, children, sub }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', letterSpacing: 0.3, mb: 0.25 }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: FS, fontWeight: 500, wordBreak: 'break-word' }}>{children}</Typography>
    {sub && <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>}
  </Box>
);

// The selected card's facts; who added it and when come from its CREATED event when there is one
const CardDetails = ({ card, events }) => {
  const added = events.find((e) => e.action === 'CREATED');
  const lastChange = events.find((e) => e.action === 'UPDATED');
  const expired = isCardExpired(card.expiry);
  const addedAt = added?.occurredAt || card.createdAt;
  return (
    <Box>
      {expired && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.25, py: 0.75, borderRadius: 1, bgcolor: 'rgba(211,47,47,0.08)', border: '1px solid rgba(211,47,47,0.25)' }}>
          <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>
            Expired {card.expiry} — ask the customer for the new card before using it.
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 2, rowGap: 1.75 }}>
        <Field label="Card">{NETWORK_NAMES[card.cardType] || card.cardType} •••• {card.lastFour}</Field>
        <Field label="Bank">{card.bankIssuer || '—'}</Field>
        <Field label="Cardholder">{card.cardholderName || '—'}</Field>
        <Field label="Expiry">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {card.expiry || 'Not given'}
            {card.expiry && !expired && (
              <Box component="span" sx={{ fontSize: '0.625rem', fontWeight: 700, px: 0.6, borderRadius: 0.75, color: 'success.main', bgcolor: 'rgba(46,125,50,0.1)' }}>VALID</Box>
            )}
          </Box>
        </Field>
        <Field label="Added" sub={[added?.actorName, addedAt && formatDateTime(addedAt)].filter(Boolean).join(' · ') || null}>
          {card.addedBy === 'TC' ? 'By the typing centre' : 'On the customer portal'}
        </Field>
        <Field label="Last changed" sub={lastChange ? `by ${lastChange.actorName || 'Unknown'}` : null}>
          {lastChange ? formatDateTime(lastChange.occurredAt) : 'Never'}
        </Field>
      </Box>
    </Box>
  );
};

// canWrite / canRemove / canSeePayments: finance write / delete / read - adding and editing, removing, and the
// cards' activity and payments.
const ClientCardsTab = ({ orgId, canWrite, canRemove, canSeePayments }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // { card } — card null = add
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [activity, setActivity] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // the card whose activity is shown; null = all cards
  const [txCard, setTxCard] = useState(null); // the card whose payments are shown

  const load = useCallback(async () => {
    setLoading(true);
    if (canSeePayments) customerCardService.activityForOrganization(orgId).then(setActivity).catch(() => setActivity([]));
    try {
      setCards(await customerCardService.listForOrganization(orgId));
    } catch (e) {
      setError(serverMessage(e, 'Failed to load cards'));
    } finally {
      setLoading(false);
    }
  }, [orgId, canSeePayments]);

  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setSaving(true);
    setError(null);
    try {
      if (dialog.card) await customerCardService.updateForOrganization(orgId, dialog.card.id, body);
      else await customerCardService.createForOrganization(orgId, body);
      setDialog(null);
      await load();
    } catch (e) {
      setError(serverMessage(e, 'Failed to save the card'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await customerCardService.removeForOrganization(orgId, removing.id);
      if (selectedId === removing.id) setSelectedId(null);
      await load();
    } catch (e) {
      setError(serverMessage(e, 'Failed to remove the card'));
    }
    setRemoving(null);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const selectedCard = cards.find((c) => c.id === selectedId) || null;
  const shownActivity = selectedCard ? activity.filter((e) => e.cardId === selectedCard.id) : activity;
  const pickCard = (id) => setSelectedId((prev) => (prev === id ? null : id));

  const timeline = shownActivity.length === 0 ? (
    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
      {selectedCard
        ? (selectedCard.addedBy === 'TC' ? 'No changes recorded for this card.' : 'Added on the customer\'s portal — no changes recorded since.')
        : 'No changes recorded yet. Every card added, changed or removed — here or on the portal — is listed with who did it.'}
    </Typography>
  ) : (
    <>
      {!selectedCard && cards.length > 0 && (
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mb: 1.25 }}>
          Click a card above to see its details and activity.
        </Typography>
      )}
      {shownActivity.map((e, idx) => (
        <TimelineRow
          key={e.id}
          e={e}
          card={cards.find((c) => c.id === e.cardId)}
          showCard={!selectedCard}
          last={idx === shownActivity.length - 1}
          onPickCard={setSelectedId}
        />
      ))}
    </>
  );

  const detailPanel = selectedCard && (
    <Paper variant="outlined" sx={{ width: { xs: '100%', md: 440 }, flexShrink: 0, overflow: 'hidden', position: { md: 'sticky' }, top: { md: 16 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2, pr: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Swatch card={selectedCard} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: FS, fontWeight: 600 }} noWrap>{selectedCard.nickname}</Typography>
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>•••• {selectedCard.lastFour}</Typography>
        </Box>
        {canSeePayments && (
          <Tooltip title="Transactions">
            <IconButton size="small" onClick={() => setTxCard(selectedCard)} aria-label="Card transactions"><HistoryIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        {canWrite && (
          <>
            <Tooltip title="Edit nickname / expiry">
              <IconButton size="small" onClick={() => setDialog({ card: selectedCard })} aria-label="Edit card"><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            {canRemove && (
              <Tooltip title="Remove">
                <IconButton size="small" color="error" onClick={() => setRemoving(selectedCard)} aria-label="Remove card"><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </>
        )}
        <Tooltip title="Close">
          <IconButton size="small" onClick={() => setSelectedId(null)} aria-label="Close card details"><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>
        <CardDetails card={selectedCard} events={shownActivity} />
      </Box>
      {canSeePayments && (
        <Box sx={{ px: 2, pt: 1.25, pb: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 1 }}>
            ACTIVITY · {shownActivity.length}
          </Typography>
          {timeline}
        </Box>
      )}
    </Paper>
  );

  const addButton = canWrite && (
    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog({ card: null })}
      sx={{ textTransform: 'none', fontSize: FS }}>
      Add Card
    </Button>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap', minHeight: 32 }}>
        <Typography sx={{ fontSize: FS, fontWeight: 600 }}>Saved cards</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 'auto' }}>
          {cards.length} {cards.length === 1 ? 'card' : 'cards'}
        </Typography>
        {cards.length > 0 && addButton}
      </Box>

      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
        Pick one of these on a work order or invoice when a government fee is paid with the customer&apos;s card.
        The customer can also add cards from their portal.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {cards.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <CardIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: FS }}>No saved cards</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: canWrite ? 2 : 0 }}>
            Add the cards this customer pays government fees with — bank, name, last 4 digits and expiry only.
          </Typography>
          {addButton}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0, width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 2 }}>
          {cards.map((card) => {
            const expired = isCardExpired(card.expiry);
            return (
              <Box key={card.id}>
                <Box sx={{ maxWidth: 280 }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedId === card.id}
                    aria-label={`Show activity of ${card.nickname}`}
                    onClick={() => pickCard(card.id)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pickCard(card.id); } }}
                    sx={{
                      borderRadius: 2, cursor: 'pointer', transition: 'transform .15s, box-shadow .15s',
                      outline: selectedId === card.id ? '2px solid' : 'none',
                      outlineColor: 'primary.main', outlineOffset: '2px',
                      '&:hover': { transform: 'translateY(-2px)' },
                      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light', outlineOffset: '2px' },
                    }}
                  >
                    <CardFace card={card} expired={expired} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 0.25 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: FS }} noWrap title={card.nickname}>{card.nickname}</Typography>
                      <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                        {card.addedBy === 'TC' ? 'Added by you' : 'From the portal'}
                        {expired && <Box component="span" sx={{ color: 'error.main' }}> · expired</Box>}
                      </Typography>
                    </Box>
                    {canSeePayments && (
                      <Tooltip title="Transactions">
                        <IconButton size="small" onClick={() => setTxCard(card)} aria-label="Card transactions"><HistoryIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {canWrite && (<>
                      <Tooltip title="Edit nickname / expiry">
                        <IconButton size="small" onClick={() => setDialog({ card })} aria-label="Edit card"><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      {canRemove && (
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => setRemoving(card)} aria-label="Remove card"><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </>)}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        {detailPanel}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 2, px: 0.5 }}>
        <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.3 }} />
        <Typography variant="caption" color="text.secondary">
          Thaklis stores only the card type, last 4 digits, bank, cardholder, nickname and expiry — never the full card
          number or CVV. If you need the full card to pay a fee, ask the customer at the time; don&apos;t write it anywhere in Thaklis.
        </Typography>
      </Box>

      {/* Card activity across every card of this customer (removed ones included); a selected card shows its own beside the cards */}
      {!selectedCard && canSeePayments && (
        <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: FS, fontWeight: 600 }}>Card activity</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}>All cards</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 'auto' }}>
              {shownActivity.length} {shownActivity.length === 1 ? 'event' : 'events'}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1.5 }}>{timeline}</Box>
        </Paper>
      )}

      <CustomerCardDialog
        open={!!dialog}
        card={dialog?.card || null}
        saving={saving}
        onClose={() => setDialog(null)}
        onSave={save}
        note="Never type the full card number or CVV — not here, not in notes. Last 4 digits only."
      />

      <CardTransactionsDialog
        open={!!txCard}
        card={txCard}
        onClose={() => setTxCard(null)}
        load={customerCardService.getTransactionsForCard}
        documentPath={(t) => (t.documentType === 'INVOICE' ? `/invoices/${t.documentId}` : `/work-orders/${t.documentId}`)}
        showStatus
      />

      <Dialog open={!!removing} onClose={() => setRemoving(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Remove card</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: FS }}>
            Remove {removing?.nickname} (*{removing?.lastFour})? It won&apos;t be offered on new payments; past payments keep it.
            {removing?.addedBy !== 'TC' && ' The customer added this card on their portal — it disappears there too.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoving(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove} sx={{ textTransform: 'none' }}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientCardsTab;
